/* Pipeline 172X: auth UI components
   API: POST /auth/email, POST /auth/verify, POST /auth/resend,
        POST /auth/oauth/google, POST /auth/oauth/linkedin,
        POST /auth/logout, DELETE /auth/sessions
   No OAuth tokens, provider scopes, or sensitive data in client code, URLs, or analytics. */

import type { CSSProperties, ReactNode } from "react";
import { BrandWordmark } from "../BrandWordmark";

/* ─── AuthShell ───────────────────────────────────────────────────── */
/** Centered card shell for all auth pages. */
export function AuthShell({
  title,
  subtitle,
  children,
  onBack,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onBack?: () => void;
}) {
  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      {/* Brand */}
      <a
        href="/"
        aria-label="172X Pipeline, return to home"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 32,
          textDecoration: "none",
        }}
        onClick={(e) => {
          e.preventDefault();
          onBack?.();
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 8,
            background: "#1a3a5c",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: 13,
              color: "#fff",
              letterSpacing: "-0.03em",
            }}
          >
            172
          </span>
        </div>
        <BrandWordmark
          decorative
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 15,
            color: "#1a3a5c",
            letterSpacing: "-0.02em",
          }}
        />
      </a>

      {/* Card */}
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          padding: "32px 32px",
          boxShadow: "0 4px 24px rgba(26,58,92,0.07)",
        }}
      >
        <h1
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 700,
            fontSize: 20,
            color: "#111827",
            letterSpacing: "-0.02em",
            marginBottom: subtitle ? 8 : 24,
            textAlign: "center",
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13.5,
              color: "#6b7280",
              textAlign: "center",
              lineHeight: 1.55,
              marginBottom: 24,
            }}
          >
            {subtitle}
          </p>
        )}
        {children}
      </div>

      {/* Legal */}
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 11.5,
          color: "#9ca3af",
          textAlign: "center",
          marginTop: 20,
          maxWidth: 320,
          lineHeight: 1.5,
        }}
      >
        By continuing, you agree to the{" "}
        <a
          href="https://172x.ai/terms"
          style={{ color: "#1a3a5c", textDecoration: "underline" }}
        >
          Terms
        </a>{" "}
        and{" "}
        <a
          href="https://172x.ai/privacy"
          style={{ color: "#1a3a5c", textDecoration: "underline" }}
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}

/* ─── ProviderButton ──────────────────────────────────────────────── */
/** OAuth provider button with accessible labels and official brand icons. */
export function ProviderButton({
  provider,
  onClick,
  isLoading = false,
  disabled = false,
  disabledReason,
}: {
  provider: "google" | "linkedin";
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const config = {
    google: {
      label: "Continue with Google",
      loadingLabel: "Redirecting to Google…",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
          role="img"
        >
          <title>Google</title>
          <path
            d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
            fill="#4285F4"
          />
          <path
            d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
            fill="#34A853"
          />
          <path
            d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
            fill="#FBBC05"
          />
          <path
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
            fill="#EA4335"
          />
        </svg>
      ),
    },
    linkedin: {
      label: "Continue with LinkedIn",
      loadingLabel: "Redirecting to LinkedIn…",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
          role="img"
        >
          <title>LinkedIn</title>
          <rect width="18" height="18" rx="3" fill="#0A66C2" />
          <path
            d="M5.37 7.45H3.27v5.82h2.1V7.45ZM4.32 6.54a1.22 1.22 0 1 0 0-2.44 1.22 1.22 0 0 0 0 2.44ZM14.73 13.27h-2.1v-2.83c0-.78-.28-1.31-.97-1.31-.53 0-.84.36-.98.71-.05.12-.06.29-.06.46v2.97h-2.1s.03-4.82 0-5.82h2.1v.82c.28-.43.78-1.04 1.9-1.04 1.38 0 2.21.9 2.21 2.84v3.2Z"
            fill="white"
          />
        </svg>
      ),
    },
  };
  const c = config[provider];
  const unavailable = disabled && !isLoading;
  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      aria-label={
        unavailable && disabledReason
          ? `${c.label}. ${disabledReason}`
          : isLoading
            ? c.loadingLabel
            : c.label
      }
      title={unavailable ? disabledReason : undefined}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        fontFamily: "Inter, sans-serif",
        fontWeight: 500,
        fontSize: 14,
        color: "#111827",
        background: "#ffffff",
        border: "1px solid #d1d5db",
        borderRadius: 9,
        padding: "11px 16px",
        cursor: isLoading || disabled ? "not-allowed" : "pointer",
        opacity: isLoading || disabled ? 0.62 : 1,
        transition: "background 0.15s, border-color 0.15s",
      }}
      onMouseEnter={(e) => {
        if (!isLoading && !disabled)
          e.currentTarget.style.background = "#f9fafb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#ffffff";
      }}
    >
      {c.icon}
      {isLoading ? c.loadingLabel : c.label}
    </button>
  );
}

/* ─── OrDivider ───────────────────────────────────────────────────── */
export function OrDivider() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        margin: "16px 0",
      }}
      role="separator"
      aria-label="or"
    >
      <div
        style={{ flex: 1, height: 1, background: "#e5e7eb" }}
        aria-hidden="true"
      />
      <span
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          color: "#9ca3af",
        }}
      >
        or continue with email
      </span>
      <div
        style={{ flex: 1, height: 1, background: "#e5e7eb" }}
        aria-hidden="true"
      />
    </div>
  );
}

/* ─── EmailInput ──────────────────────────────────────────────────── */
export function EmailInput({
  value,
  onChange,
  error,
  disabled = false,
  autoFocus = false,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor="auth-email"
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 12.5,
          fontWeight: 500,
          color: "#374151",
          display: "block",
          marginBottom: 5,
        }}
      >
        Email address
      </label>
      <input
        id="auth-email"
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="email"
        autoFocus={autoFocus}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? "auth-email-error" : undefined}
        placeholder="you@example.com"
        style={{
          width: "100%",
          boxSizing: "border-box",
          fontFamily: "Inter, sans-serif",
          fontSize: 14,
          color: "#111827",
          background: disabled ? "#f9fafb" : "#ffffff",
          border: `1.5px solid ${error ? "#dc2626" : "#d1d5db"}`,
          borderRadius: 8,
          padding: "10px 13px",
          outline: "none",
        }}
        onFocus={(e) => {
          if (!error) e.target.style.borderColor = "#1a3a5c";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? "#dc2626" : "#d1d5db";
        }}
      />
      {error && (
        <p
          id="auth-email-error"
          role="alert"
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "#dc2626",
            marginTop: 4,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/* ─── PrimaryAuthButton ───────────────────────────────────────────── */
export function PrimaryAuthButton({
  children,
  onClick,
  isLoading = false,
  disabled = false,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  const unavailable = isLoading || disabled;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={unavailable}
      style={{
        width: "100%",
        fontFamily: "Inter, sans-serif",
        fontWeight: 600,
        fontSize: 14,
        color: "#ffffff",
        background: unavailable ? "#4b7ea8" : "#1a3a5c",
        border: "none",
        borderRadius: 9,
        padding: "12px 16px",
        cursor: unavailable ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => {
        if (!unavailable) {
          e.currentTarget.style.background = "#1e4a7a";
        }
      }}
      onMouseLeave={(e) => {
        if (!unavailable) {
          e.currentTarget.style.background = "#1a3a5c";
        }
      }}
    >
      {isLoading && (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
          style={{ animation: "spin 1s linear infinite" }}
        >
          <circle
            cx="7"
            cy="7"
            r="5.5"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2"
          />
          <path
            d="M7 1.5A5.5 5.5 0 0 1 12.5 7"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
      {children}
    </button>
  );
}

/* ─── AuthLink ────────────────────────────────────────────────────── */
export function AuthLink({
  children,
  onClick,
  href,
}: {
  children: ReactNode;
  onClick?: () => void;
  href?: string;
}) {
  const style: CSSProperties = {
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    color: "#1a3a5c",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  };

  if (href) {
    return (
      <a href={href} style={style}>
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} style={style}>
      {children}
    </button>
  );
}

/* ─── VerificationPending ─────────────────────────────────────────── */
export function VerificationPending({
  email,
  onResend,
  onChangeEmail,
  resendAvailable = true,
}: {
  email: string;
  onResend: () => void;
  onChangeEmail: () => void;
  resendAvailable?: boolean;
}) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 13,
          background: "#eff6ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
        }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M4 4h16v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Z"
            stroke="#1a3a5c"
            strokeWidth="1.5"
          />
          <path
            d="M4 4l8 9 8-9"
            stroke="#1a3a5c"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13.5,
          color: "#4b5563",
          lineHeight: 1.6,
          marginBottom: 20,
        }}
      >
        If this email can be used, we&apos;ve sent the next step. Check your
        inbox and click the link to continue.
      </p>
      <p
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 11,
          color: "#9ca3af",
          marginBottom: 4,
          letterSpacing: "0.01em",
        }}
      >
        {email}
      </p>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          color: "#9ca3af",
          marginBottom: 24,
          lineHeight: 1.5,
        }}
      >
        The link expires after 24 hours. Check your spam folder if it
        doesn&apos;t arrive.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {resendAvailable && (
          <button
            onClick={onResend}
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: 13.5,
              color: "#1a3a5c",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              padding: "10px 16px",
              cursor: "pointer",
            }}
          >
            Resend verification email
          </button>
        )}
        <button
          onClick={onChangeEmail}
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: "#6b7280",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 4,
          }}
        >
          Use a different email address
        </button>
      </div>
    </div>
  );
}

/* ─── VerificationExpired ─────────────────────────────────────────── */
export function VerificationExpired({ onRestart }: { onRestart: () => void }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 13,
          background: "#fef2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
        }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="#dc2626" strokeWidth="1.5" />
          <path
            d="M12 7v5M12 14v1"
            stroke="#dc2626"
            strokeWidth="1.75"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13.5,
          color: "#4b5563",
          lineHeight: 1.6,
          marginBottom: 20,
        }}
      >
        This verification link has expired or is invalid. Request a new link to
        continue.
      </p>
      <button
        onClick={onRestart}
        style={{
          width: "100%",
          fontFamily: "Inter, sans-serif",
          fontWeight: 600,
          fontSize: 14,
          color: "#fff",
          background: "#1a3a5c",
          border: "none",
          borderRadius: 9,
          padding: "12px 16px",
          cursor: "pointer",
        }}
      >
        Restart sign-up
      </button>
    </div>
  );
}

/* ─── OAuthErrorState ─────────────────────────────────────────────── */
export function OAuthErrorState({
  error,
  onRetry,
  onAlternate,
}: {
  error:
    | "cancelled"
    | "provider-denied"
    | "provider-unavailable"
    | "missing-email"
    | "existing-account";
  onRetry: () => void;
  onAlternate: () => void;
}) {
  const messages: Record<typeof error, { heading: string; body: string }> = {
    cancelled: {
      heading: "Sign-in cancelled",
      body: "You cancelled the sign-in flow. You can try again or use a different method.",
    },
    "provider-denied": {
      heading: "Access wasn't granted",
      body: "The provider did not grant access. Make sure to approve the required permissions and try again.",
    },
    "provider-unavailable": {
      heading: "Provider temporarily unavailable",
      body: "The authentication provider is not available right now. Please try again shortly or use email.",
    },
    "missing-email": {
      heading: "Email not available",
      body: "Your provider did not return a verified email address. Please use email sign-in or verify your provider email first.",
    },
    "existing-account": {
      heading: "Account already exists",
      body: "An account with this email already exists. Sign in with your existing method, or we can securely link this provider to your account.",
    },
  };
  const { heading, body } = messages[error];
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 13,
          background: "#fffbeb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
        }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3L2 20h20L12 3ZM12 9v5M12 16.5v.5"
            stroke="#b45309"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fontSize: 17,
          color: "#111827",
          marginBottom: 10,
        }}
      >
        {heading}
      </h2>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13.5,
          color: "#4b5563",
          lineHeight: 1.6,
          marginBottom: 24,
        }}
      >
        {body}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          onClick={onRetry}
          style={{
            width: "100%",
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            color: "#fff",
            background: "#1a3a5c",
            border: "none",
            borderRadius: 9,
            padding: "12px 16px",
            cursor: "pointer",
          }}
        >
          Try again
        </button>
        <button
          onClick={onAlternate}
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: "#1a3a5c",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 4,
            textDecoration: "underline",
          }}
        >
          Use a different method
        </button>
      </div>
    </div>
  );
}

/* ─── SessionExpiredBanner ────────────────────────────────────────── */
export function SessionExpiredBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="alert"
      style={{
        padding: "12px 16px",
        background: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: 9,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 20,
      }}
    >
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13,
          color: "#92400e",
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        Your session has expired. Please sign in again to continue.
      </p>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#92400e",
          flexShrink: 0,
          padding: 0,
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 4l8 8M12 4l-8 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

/* ─── MarketingConsentControl ─────────────────────────────────────── */
export function MarketingConsentControl({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, flexShrink: 0 }}
      />
      <span
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 12.5,
          color: "#6b7280",
          lineHeight: 1.5,
        }}
      >
        I want to receive occasional product updates and tips. You can
        unsubscribe at any time.
        <span style={{ color: "#9ca3af" }}> (Optional)</span>
      </span>
    </label>
  );
}

/* ─── spin keyframe via global style ─────────────────────────────── */
const spinStyle = document.createElement("style");
spinStyle.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
if (
  typeof document !== "undefined" &&
  !document.getElementById("auth-spin-style")
) {
  spinStyle.id = "auth-spin-style";
  document.head.appendChild(spinStyle);
}
